// Portal role-selection persistence.
// Selected clinicId and ohtId are client context for the chosen assignment.
// Clinic Admin requests send clinicId so the server can authorize it.
// Stored ohtId is sent only as a requested scope. The server must authorize it.

export const PORTAL_ROLE_CHOICE_KEY = "ocf.portal.roleChoice";
export const PORTAL_CLINIC_ADMIN_CLINIC_ID_KEY = "ocf.portal.clinicAdminClinicId";
export const PORTAL_OHT_ID_KEY = "ocf.portal.ohtId";
export const CLINIC_ADMIN_SCOPE_HEADER = "x-acceptingmd-clinic-id";
export const OHT_SCOPE_HEADER = "x-acceptingmd-oht-id";

export type PortalRoleAssignment = {
  roleName?: string | null;
  clinicId?: number | string | null;
  clinicName?: string | null;
  ohtId?: number | string | null;
  ohtName?: string | null;
  ohtCode?: string | null;
  isActive?: boolean | number | string | null;
};

export type StoredPortalAssignment = {
  roleName: string | null;
  clinicId: number | null;
  ohtId: number | null;
};

export function normRoleName(name: unknown): string {
  return String(name || "").trim().toLowerCase();
}

/** Parse a stored/requested clinic ID. "12" -> 12. Rejects 0, negatives, decimals, empty. */
export function parsePositiveInt(value: unknown): number | null {
  if (value === null || value === undefined) return null;

  const text = String(value).trim();
  if (!text) return null;
  if (!/^[1-9]\d*$/.test(text)) return null;

  const n = Number.parseInt(text, 10);
  if (!Number.isSafeInteger(n) || n <= 0) return null;

  return n;
}

export function getStoredPortalRoleChoice(): string | null {
  try {
    const n = normRoleName(window.localStorage.getItem(PORTAL_ROLE_CHOICE_KEY));
    return n || null;
  } catch {
    return null;
  }
}

export function getStoredClinicAdminClinicId(): number | null {
  try {
    return parsePositiveInt(
      window.localStorage.getItem(PORTAL_CLINIC_ADMIN_CLINIC_ID_KEY)
    );
  } catch {
    return null;
  }
}

export function clearStoredClinicAdminClinicId(): void {
  try {
    window.localStorage.removeItem(PORTAL_CLINIC_ADMIN_CLINIC_ID_KEY);
  } catch {
    // ignore
  }
}

export function getStoredOhtId(): number | null {
  try {
    return parsePositiveInt(window.localStorage.getItem(PORTAL_OHT_ID_KEY));
  } catch {
    return null;
  }
}

export function clearStoredOhtId(): void {
  try {
    window.localStorage.removeItem(PORTAL_OHT_ID_KEY);
  } catch {
    // ignore
  }
}

export function isOhtScopedRoleName(roleName: unknown): boolean {
  const roleKey = normRoleName(roleName);
  return roleKey === "ohtadmin" || roleKey === "communitynavigator";
}

function writeRoleName(roleKey: string): void {
  try {
    if (roleKey) {
      window.localStorage.setItem(PORTAL_ROLE_CHOICE_KEY, roleKey);
    } else {
      window.localStorage.removeItem(PORTAL_ROLE_CHOICE_KEY);
    }
  } catch {
    // ignore
  }
}

function writeOhtId(ohtId: number): void {
  try {
    window.localStorage.setItem(PORTAL_OHT_ID_KEY, String(ohtId));
  } catch {
    // ignore
  }
}

/**
 * Persist the selected portal role.
 * Clinic Admin also stores clinicId. OHT Admin and Community Navigator store ohtId.
 * Switching role families clears the scope that does not apply.
 * An OHT-scoped role without an OHT id is not stored.
 */
export function setStoredPortalRoleChoice(
  roleName: string,
  clinicId?: number | null,
  ohtId?: number | null
): void {
  const roleKey = normRoleName(roleName);

  if (isOhtScopedRoleName(roleKey)) {
    clearStoredClinicAdminClinicId();
    const id = parsePositiveInt(ohtId);
    if (id === null) {
      clearStoredOhtId();
      writeRoleName("");
      return;
    }

    writeRoleName(roleKey);
    writeOhtId(id);
    return;
  }

  writeRoleName(roleKey);
  clearStoredOhtId();

  if (roleKey === "clinicadmin") {
    const id = parsePositiveInt(clinicId);
    if (id === null) {
      clearStoredClinicAdminClinicId();
      return;
    }

    try {
      window.localStorage.setItem(PORTAL_CLINIC_ADMIN_CLINIC_ID_KEY, String(id));
    } catch {
      // ignore
    }
    return;
  }

  clearStoredClinicAdminClinicId();
}

export function clearStoredPortalRoleChoice(): void {
  try {
    window.localStorage.removeItem(PORTAL_ROLE_CHOICE_KEY);
  } catch {
    // ignore
  }
  clearStoredClinicAdminClinicId();
  clearStoredOhtId();
}

export function getStoredPortalAssignment(): StoredPortalAssignment {
  return {
    roleName: getStoredPortalRoleChoice(),
    clinicId: getStoredClinicAdminClinicId(),
    ohtId: getStoredOhtId(),
  };
}

/**
 * Identity of one selectable assignment.
 * Clinic Admin stays distinct by clinic. OHT-scoped roles are distinct by OHT.
 * An OHT-scoped role without an OHT id is not a global assignment.
 */
export function assignmentKey(role: PortalRoleAssignment | null | undefined): string | null {
  const name = normRoleName(role?.roleName);
  if (!name) return null;

  if (name === "clinicadmin") {
    const clinicId = parsePositiveInt(role?.clinicId);
    return clinicId !== null ? `${name}:${clinicId}` : name;
  }

  if (isOhtScopedRoleName(name)) {
    const ohtId = parsePositiveInt(role?.ohtId);
    return ohtId !== null ? `${name}:${ohtId}` : null;
  }

  return name;
}

export function isPortalRoleActive(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return String(value).trim().toLowerCase() === "true";
}

export function distinctSelectableAssignments<T extends PortalRoleAssignment>(
  roles: T[] | null | undefined,
  isActive: (role: T) => boolean = (role) => isPortalRoleActive(role.isActive)
): T[] {
  const unique = new Map<string, T>();

  for (const role of roles || []) {
    if (!isActive(role)) continue;
    const key = assignmentKey(role);
    if (!key || unique.has(key)) continue;
    unique.set(key, role);
  }

  return Array.from(unique.values());
}

export function persistPortalAssignment(role: PortalRoleAssignment | null | undefined): boolean {
  const roleKey = normRoleName(role?.roleName);
  if (!roleKey) return false;

  if (roleKey === "clinicadmin") {
    const clinicId = parsePositiveInt(role?.clinicId);
    if (clinicId === null) return false;
    setStoredPortalRoleChoice(roleKey, clinicId, null);
    return true;
  }

  if (isOhtScopedRoleName(roleKey)) {
    const ohtId = parsePositiveInt(role?.ohtId);
    if (ohtId === null) return false;
    setStoredPortalRoleChoice(roleKey, null, ohtId);
    return true;
  }

  setStoredPortalRoleChoice(roleKey, null, null);
  return true;
}

/**
 * Restore only an assignment that is still present.
 * A stored OHT that is gone is not replaced with another OHT for the same role.
 */
export function findStoredAssignment<T extends PortalRoleAssignment>(
  assignments: T[] | null | undefined,
  stored: StoredPortalAssignment
): T | null {
  const roleName = normRoleName(stored.roleName);
  if (!roleName) return null;

  if (roleName === "clinicadmin") {
    if (stored.clinicId === null) return null;
    return (
      (assignments || []).find(
        (role) => assignmentKey(role) === `clinicadmin:${stored.clinicId}`
      ) || null
    );
  }

  if (isOhtScopedRoleName(roleName)) {
    if (stored.ohtId === null) return null;
    const key = `${roleName}:${stored.ohtId}`;
    return (assignments || []).find((role) => assignmentKey(role) === key) || null;
  }

  return (
    (assignments || []).find((role) => assignmentKey(role) === roleName) || null
  );
}

export function assignmentSubtitle(role: PortalRoleAssignment | null | undefined): string | null {
  const roleName = normRoleName(role?.roleName);

  if (roleName === "clinicadmin") {
    const clinicName = String(role?.clinicName || "").trim();
    if (clinicName) return clinicName;
    const clinicId = parsePositiveInt(role?.clinicId);
    return clinicId !== null ? `Clinic #${clinicId}` : null;
  }

  if (!isOhtScopedRoleName(roleName)) return null;

  const ohtName = String(role?.ohtName || "").trim();
  if (ohtName) return ohtName;

  const ohtCode = String(role?.ohtCode || "").trim();
  if (ohtCode) return ohtCode;

  const ohtId = parsePositiveInt(role?.ohtId);
  return ohtId !== null ? `OHT #${ohtId}` : null;
}

/**
 * Headers to send on Clinic Admin API requests.
 * Returns {} when no valid selected clinic ID exists — never sends
 * undefined/null/malformed values.
 */
export function clinicAdminScopeHeaders(): Record<string, string> {
  const clinicId = getStoredClinicAdminClinicId();
  if (clinicId === null) return {};
  return { [CLINIC_ADMIN_SCOPE_HEADER]: String(clinicId) };
}

/**
 * Requested OHT scope for the selected OHT Admin or Community Navigator assignment.
 * Empty for every other role. This header does not grant access.
 */
export function ohtScopeHeaders(): Record<string, string> {
  if (!isOhtScopedRoleName(getStoredPortalRoleChoice())) return {};
  const ohtId = getStoredOhtId();
  if (ohtId === null) return {};
  return { [OHT_SCOPE_HEADER]: String(ohtId) };
}

function plainHeaders(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) return {};
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return { ...headers };
}

/** Merge the selected OHT scope onto a portal request. Does not add a clinic header. */
export function withOhtScope(init?: RequestInit): RequestInit {
  const source = init || {};
  return {
    ...source,
    credentials: source.credentials ?? "include",
    headers: {
      ...plainHeaders(source.headers),
      ...ohtScopeHeaders(),
    },
  };
}

export function portalFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return fetch(input, withOhtScope(init));
}
